// @givmo/design-system — Calendar (shadcn-style Calendar on react-day-picker, themed with the
// 03-3a tokens).
//
// The Flutter super-admin used Material `showDatePicker` in a few places (recon §6 / the plan).
// On web we use react-day-picker's <DayPicker> (the shadcn Calendar foundation) and theme it
// entirely through the `classNames` prop with the Givmo tokens — we do NOT import the library's
// own `style.css`, so the calendar inherits no foreign styling and is driven by tokens only:
//   - selected day  = blue (#3C6DE5) fill, white text, radius all12 (the kit's input radius).
//   - today / hover = blue20 (#DEEBF6) tint.
//   - typography    = Lato, s14 body / s16 caption, tracking -0.3 (the kit base).
//   - nav chevrons  = Lucide, gray.
//   - month/year lists (`captionLayout="dropdown"`, opt-in) = the caption's own words with a down
//     chevron over an invisible native <select>, so the OS list does the choosing.
//
// This is the calendar surface only. The full popover-trigger-wrapped date picker uses Base UI's
// Popover, which is owned by slice 03-3c; <DatePicker> (date-picker.tsx) ships an interim
// lightweight input-trigger now and will adopt the 03-3c Popover later. `testId` forwards to the
// DayPicker root.
import { DayPicker } from 'react-day-picker';
import type { DayPickerProps } from 'react-day-picker';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export type CalendarProps = DayPickerProps & TestIdProps;

export function Calendar({ className, classNames, testId, ...props }: CalendarProps) {
  return (
    <DayPicker
      className={cn('inline-block p-12 font-sans tracking-givmo text-black', className)}
      classNames={{
        months: 'flex flex-col',
        month: 'flex flex-col gap-8',
        month_caption: 'flex items-center justify-center h-[36px]',
        caption_label: 'inline-flex items-center gap-2 text-s16 font-w600',
        dropdowns: 'flex items-center gap-4',
        dropdown_root:
          'relative inline-flex items-center rounded-all12 px-6 py-2 hover:bg-blue20 has-[select:focus-visible]:shadow-focus-glow',
        dropdown: 'absolute inset-0 w-full cursor-pointer opacity-0',
        nav: 'flex items-center justify-between absolute inset-x-0 top-12 px-12',
        button_previous:
          'inline-flex size-[28px] items-center justify-center rounded-all100 text-gray80 hover:bg-blue20 outline-none focus-visible:shadow-focus-glow disabled:opacity-40',
        button_next:
          'inline-flex size-[28px] items-center justify-center rounded-all100 text-gray80 hover:bg-blue20 outline-none focus-visible:shadow-focus-glow disabled:opacity-40',
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'w-[36px] text-s12 font-w600 text-gray60',
        week: 'flex w-full mt-2',
        day: 'size-[36px] p-0 text-center',
        day_button:
          'inline-flex size-[36px] items-center justify-center rounded-all12 text-s14 font-w400 hover:bg-blue20 outline-none focus-visible:shadow-focus-glow',
        selected: '[&>button]:bg-blue [&>button]:text-on-brand [&>button]:hover:bg-blue',
        today: '[&>button]:bg-blue20 [&>button]:font-w700',
        outside: 'text-gray60',
        disabled: 'text-gray40 [&>button]:cursor-not-allowed [&>button]:hover:bg-transparent',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName }) =>
          orientation === 'left' ? (
            <ChevronLeft size={18} className={chevronClassName} aria-hidden />
          ) : orientation === 'down' ? (
            <ChevronDown size={16} className={cn('text-gray80', chevronClassName)} aria-hidden />
          ) : (
            <ChevronRight size={18} className={chevronClassName} aria-hidden />
          ),
      }}
      {...testIdProp(testId)}
      {...props}
    />
  );
}
